import { View, Text, StyleSheet, ScrollView, Pressable, Linking, Alert } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { API_BASE_URL } from "../config";

const RegistrationDetails = ({ userData, title, fileFields = [] }) => {

  
  // Fields to exclude from display
  const excludedFields = [
    "User_ID",
    "Registration_Number",
    "Scanned",
    "Recognized",
    "Updated_AT",
    "Created_AT",
    "QRPath",
  ];

  // Function to format field names for display
  const formatFieldName = (fieldName) => {
    // Convert camelCase or snake_case to Title Case with spaces
    return fieldName
      .replace(/_/g, " ")
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  // Function to get file icon based on file extension
  const getFileIcon = (filePath) => {
    if (!filePath || typeof filePath !== 'string') return "document-outline";
    
    const extension = filePath.toLowerCase().split('.').pop();
    
    switch (extension) {
      case 'pdf':
        return "document-text-outline";
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'gif':
      case 'bmp':
        return "image-outline";
      case 'doc':
      case 'docx':
        return "document-outline";
      case 'xls':
      case 'xlsx':
        return "grid-outline";
      case 'ppt':
      case 'pptx':
        return "easel-outline";
      default:
        return "attach-outline";
    }
  };

  // Function to get file name from path
  const getFileName = (filePath) => {
    if (!filePath || typeof filePath !== 'string') return "Unknown File";
    return filePath.split('/').pop() || "Unknown File";
  };

  // Function to handle file opening
  const handleFilePress = async (filePath) => {
    try {
      if (!filePath) {
        Alert.alert("Error", "File path not available");
        return;
      }

      const fullUrl = `${API_BASE_URL}/${filePath}`;
      console.log("Opening file URL:", fullUrl);
      
      const supported = await Linking.canOpenURL(fullUrl);
      
      if (supported) {
        await Linking.openURL(fullUrl);
      } else {
        Alert.alert("Error", "Cannot open this file type");
      }
    } catch (error) {
      console.error("Error opening file:", error);
      Alert.alert("Error", "Failed to open file");
    }
  };

  // Function to check if a field is a file field
  const isFileField = (fieldName) => {
    return fileFields.includes(fieldName);
  };

  // Filter out excluded fields and prepare data for display
  const displayData = Object.entries(userData || {}).filter(
    ([key]) => !excludedFields.includes(key)
  );

  // Function to render file item
  const renderFileItem = (key, value) => (
    <View key={key} style={styles.detailItem}>
      <Text style={styles.detailLabel}>{formatFieldName(key)}</Text>
      {value && value.toString().trim() ? (
        <Pressable 
          style={styles.fileButton}
          onPress={() => handleFilePress(value)}
          android_ripple={{ color: 'rgba(255,255,255,0.2)' }}
        >
          <View style={styles.fileButtonContent}>
            <Ionicons 
              name={getFileIcon(value)} 
              size={20} 
              color="#4A90E2" 
              style={styles.fileIcon}
            />
            <View style={styles.fileTextContainer}>
              <Text style={styles.fileName}>{getFileName(value)}</Text>
              <Text style={styles.fileHint}>Tap to open</Text>
            </View>
            <Ionicons 
              name="open-outline" 
              size={16} 
              color="#4A90E2" 
            />
          </View>
        </Pressable>
      ) : (
        <Text style={styles.detailValue}>No file uploaded</Text>
      )}
    </View>
  );

  // Function to render regular item
  const renderRegularItem = (key, value) => (
    <View key={key} style={styles.detailItem}>
      <Text style={styles.detailLabel}>{formatFieldName(key)}</Text>
      <Text style={styles.detailValue}>
        {value?.toString() || "N/A"}
      </Text>
    </View>
  );

  return (
    <View style={styles.container}>
      <Text style={styles.sectionTitle}>{title}</Text>
      <ScrollView style={styles.detailsContainer} showsVerticalScrollIndicator={false}>
        {displayData.length > 0 ? (
          displayData.map(([key, value]) => 
            isFileField(key) 
              ? renderFileItem(key, value)
              : renderRegularItem(key, value)
          )
        ) : (
          <Text style={styles.noDataText}>
            No registration details available
          </Text>
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginTop: 10,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#FFFFFF",
    marginBottom: 12,
    textAlign: "center",
  },
  detailsContainer: {
    width: "100%",
    minHeight: 300,
  },
  detailItem: {
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    borderRadius: 10,
    padding: 12,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: "rgba(255, 255, 255, 0.3)",
  },
  detailLabel: {
    fontSize: 14,
    color: "#FFFFFF",
    opacity: 0.8,
    marginBottom: 4,
  },
  detailValue: {
    fontSize: 16,
    fontWeight: "500",
    color: "#FFFFFF",
  },
  noDataText: {
    color: "#FFFFFF",
    textAlign: "center",
    opacity: 0.7,
    padding: 20,
  },
  fileButton: {
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    borderRadius: 8,
    marginTop: 4,
    overflow: 'hidden',
  },
  fileButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
  },
  fileIcon: {
    marginRight: 12,
  },
  fileTextContainer: {
    flex: 1,
  },
  fileName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 2,
  },
  fileHint: {
    fontSize: 12,
    color: '#666',
    opacity: 0.8,
  },
});

export default RegistrationDetails;